import React from 'react';
import './Coachmark.css';

interface CoachmarkProps {
  heading: string;
  body: React.ReactNode;
  /** Primary dismiss button label. */
  buttonLabel: string;
  onDismiss: () => void;
  /**
   * Optional text action rendered beside the primary button. The mobile
   * treatment uses it for "Show me the filter"; desktop passes neither.
   */
  secondaryLabel?: string;
  onSecondary?: () => void;
  /**
   * Which edge the caret sits on, i.e. which way the flyout points. 'up' for a
   * flyout below its anchor, 'down' for one above it (the mobile dock case).
   */
  caret?: 'up' | 'down';
  className?: string;
}

/**
 * Anchored contextual-help flyout: purple slab, caret, one dismiss button.
 *
 * Presentational ONLY. It holds no visibility state, reads nothing, and
 * persists nothing — the caller decides when it renders and when it stops.
 * That is deliberate: this is per-navigation help, not an announcement, so
 * there is no hasSeen flag and nothing to gate. Do not add storage here; a
 * future consumer that wants once-ever behavior should gate its own render
 * (see useSmsAlertsLaunchAnnouncementGate for that shape).
 *
 * Non-blocking by construction: no overlay, no scrim, no focus trap. Callers
 * dim their own surroundings to direct attention (opacity only — nothing may
 * become pointer-events:none, the user must be able to keep working).
 *
 * Positioning belongs to the caller too. The flyout fills the width it is
 * given, so a caller inside the 344px search rail sizes it with the rail's
 * 14px insets rather than passing coordinates.
 */
const Coachmark: React.FC<CoachmarkProps> = ({
  heading,
  body,
  buttonLabel,
  onDismiss,
  secondaryLabel,
  onSecondary,
  caret = 'up',
  className,
}) => (
  <div
    className={`coachmark coachmark--caret-${caret}${className ? ` ${className}` : ''}`}
    role="status"
    data-testid="coachmark"
  >
    <span className={`coachmark-caret coachmark-caret--${caret}`} aria-hidden="true" />

    <div className="coachmark-head">
      {/* Swap arrows: the Trade Builder glyph, inlined from the same artwork the
          sidebar nav item loads as an <img>. Inlined rather than <img> so it
          takes the coachmark's white fill and costs no request. */}
      <svg
        className="coachmark-glyph"
        width="19"
        height="19"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M7 21L2 16L7 11L8.425 12.4L5.825 15H21V17H5.825L8.425 19.6L7 21ZM17 13L15.575 11.6L18.175 9H3V7H18.175L15.575 4.4L17 3L22 8L17 13Z"
          fill="currentColor"
        />
      </svg>
      <h2 className="coachmark-heading">{heading}</h2>
    </div>

    <p className="coachmark-body">{body}</p>

    <div className="coachmark-actions">
      <button type="button" className="coachmark-button" onClick={onDismiss}>
        {buttonLabel}
      </button>
      {secondaryLabel && onSecondary && (
        <button type="button" className="coachmark-secondary" onClick={onSecondary}>
          {secondaryLabel}
        </button>
      )}
    </div>
  </div>
);

export default Coachmark;
